// =============================================================================
// composables/useConnectivity.js — estado offline + cola de reintentos (RX-5)
// -----------------------------------------------------------------------------
// Expone:
//   - `isOnline`: reactivo (navigator.onLine + eventos online/offline).
//   - `retryQueue`: operaciones en cola por fallar sin conexión.
//   - `flushRetries()`: reenvía las operaciones encoladas (reintentos manuales
//     o automáticos al reconectar) y retira las exitosas.
// La cola en sí (persistencia y eventos) vive en `src/utils/offlineQueue.js`.
// =============================================================================
import { ref, onMounted, onBeforeUnmount } from 'vue';
import apiClient from '@/api/client';
import { loadQueue, clearQueueItem } from '@/utils/offlineQueue';

const isOnline = ref(typeof navigator !== 'undefined' ? navigator.onLine : true);
const retryQueue = ref(loadQueue());

const refreshQueue = () => {
  retryQueue.value = loadQueue();
};

/**
 * Reenvía las operaciones encoladas (reintentos).
 * @returns {Promise<number>} cantidad de operaciones reenviadas con éxito.
 */
const flushRetries = async () => {
  const items = loadQueue();
  let flushed = 0;

  for (const item of items) {
    try {
      await apiClient[item.method](item.url, item.data, item.config);
      clearQueueItem(item.id);
      flushed += 1;
    } catch (error) {
      // Si sigue sin conexión, detenemos el reintento en bucle; la operación
      // permanece en la cola y se reintentará en el próximo evento 'online'.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) break;
    }
    refreshQueue();
  }
  refreshQueue();
  return flushed;
};

export function useConnectivity() {
  const handleOnline = () => {
    isOnline.value = true;
    refreshQueue();
    if (retryQueue.value.length > 0) {
      flushRetries();
    }
  };

  const handleOffline = () => {
    isOnline.value = false;
  };

  const handleQueueChanged = () => refreshQueue();

  onMounted(() => {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('nexus:queue-changed', handleQueueChanged);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
    window.removeEventListener('nexus:queue-changed', handleQueueChanged);
  });

  return { isOnline, retryQueue, flushRetries };
}